import {
  AppIcon,
  type ColorToken,
  DonutChart,
  type DonutSegment,
  LabeledBarList,
  LegendRow,
  StackedBar,
  type StackedBarSegment,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import {
  type BlueprintResponse,
  type CareerPathFitCategory,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

import { careerTierLabel, percentOf, percentShares } from './analytics.labels';
import { donutWrapSx, pctChipSx, pctChipTextSx, rowLabelSx, titleSx } from './analytics.styles';
import { AnalyticsCard } from './AnalyticsCard';
import { SubsectionHeader } from './AnalyticsSubsectionHeader';
import { useAgentContext } from './BlueprintCards.agentContext';

const { blueprint } = appStrings.modules.coachingAnalytics;

const TRACK_COLORS: readonly ColorToken[] = [
  'Brand/500',
  'Warning/400',
  'Error/400',
  'Success/500',
  'Colors/Base/Black',
];

const FIT_SEGMENTS: readonly {
  key: 'strong' | 'medium' | 'low';
  label: string;
  color: ColorToken;
  labelColor: ColorToken;
}[] = [
  {
    key: 'strong',
    label: blueprint.careerPathFit.strong,
    color: 'Brand/700',
    labelColor: 'Colors/Text/text-white',
  },
  {
    key: 'medium',
    label: blueprint.careerPathFit.medium,
    color: 'Brand/500',
    labelColor: 'Colors/Text/text-white',
  },
  {
    key: 'low',
    label: blueprint.careerPathFit.low,
    color: 'Brand/200',
    labelColor: 'Colors/Text/text-primary',
  },
];

const FIT_BAR_HEIGHT = 28;

const trackColor = (index: number): ColorToken =>
  TRACK_COLORS[Math.min(index, TRACK_COLORS.length - 1)];

const UniqueBlueprintsCard = ({ data }: Readonly<{ data: BlueprintResponse }>) => (
  <AnalyticsCard
    title={blueprint.uniqueBlueprints.title}
    subtitle={blueprint.uniqueBlueprints.subtitle(data.students.total)}
    info={[blueprint.uniqueBlueprints.info, blueprint.uniqueBlueprints.entrepreneurshipInfo]}
  >
    <Typography variant="display-sm-semibold" sx={titleSx}>
      {data.unique_blueprints.count.toLocaleString()}
    </Typography>
    <LabeledBarList
      bars={[
        {
          id: 'entrepreneurship',
          label: blueprint.uniqueBlueprints.entrepreneurship,
          pct: data.entrepreneurship_recommended.percentage,
        },
      ]}
    />
  </AnalyticsCard>
);

const TargetCareerTracksCard = ({ data }: Readonly<{ data: BlueprintResponse }>) => {
  const { total, tiers } = data.target_career_tracks;
  const shares = percentShares(
    tiers.map((tier) => tier.count),
    total,
  );
  const tracks = tiers.map((tier, index) => ({
    id: tier.id,
    label: careerTierLabel(tier.id),
    pct: shares[index],
  }));
  const donutSegments: DonutSegment[] = tracks.map((track, index) => ({
    label: track.label,
    value: track.pct,
    color: trackColor(index),
  }));

  return (
    <AnalyticsCard
      title={blueprint.targetCareerTracks.title}
      subtitle={blueprint.targetCareerTracks.subtitle}
      info={[blueprint.targetCareerTracks.info]}
    >
      <Box sx={innerPanelSx}>
        <Box sx={trackLegendSx}>
          {tracks.map((track, index) => (
            <Box key={track.id} sx={trackLegendRowSx}>
              <Box sx={legendRingSx}>
                <AppIcon
                  iconName="Circle"
                  diameter="14px"
                  color="Colors/Base/Black"
                  fill={trackColor(index)}
                />
              </Box>
              <Typography variant="text-sm-medium" sx={rowLabelSx}>
                {track.label}
              </Typography>
              <Box sx={pctChipSx}>
                <Typography variant="text-xs-medium" sx={pctChipTextSx}>
                  {`${track.pct}%`}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
        <Box sx={donutWrapSx}>
          <DonutChart variant={{ type: 'multi', segments: donutSegments }} size={200} />
        </Box>
      </Box>
    </AnalyticsCard>
  );
};

const CareerPathFitRowItem = ({ row }: Readonly<{ row: CareerPathFitCategory }>) => {
  const count = row.students.strong + row.students.medium + row.students.low;
  const segments: StackedBarSegment[] = FIT_SEGMENTS.map((segment) => ({
    key: segment.key,
    pct: percentOf(row.students[segment.key], count),
    color: segment.color,
    labelColor: segment.labelColor,
  }));

  return (
    <Box sx={fitRowSx}>
      <Typography variant="text-sm-semibold" sx={fitRowLabelSx}>
        {row.name}
      </Typography>
      <Box sx={fitBarWrapSx}>
        <StackedBar segments={segments} height={FIT_BAR_HEIGHT} />
      </Box>
    </Box>
  );
};

/** Ranked by how many students fit the category strongly. */
export const byStrongFit = <T extends Readonly<{ students: Readonly<{ strong: number }> }>>(
  categories: readonly T[],
): readonly T[] => [...categories].sort((a, b) => b.students.strong - a.students.strong);

const CareerPathFitCard = ({ data }: Readonly<{ data: BlueprintResponse }>) => {
  const rows = byStrongFit(data.career_path_fit.categories);

  return (
    <AnalyticsCard
      title={blueprint.careerPathFit.title}
      subtitle={blueprint.careerPathFit.subtitle(data.career_path_fit.total)}
      info={[blueprint.careerPathFit.info]}
    >
      <Box sx={innerPanelSx}>
        <Box sx={fitLegendSx}>
          {FIT_SEGMENTS.map((segment) => (
            <LegendRow key={segment.key} color={segment.color} label={segment.label} />
          ))}
        </Box>
        <Box sx={fitRowListSx}>
          {rows.map((row) => (
            <CareerPathFitRowItem key={row.id} row={row} />
          ))}
        </Box>
      </Box>
    </AnalyticsCard>
  );
};

export const BlueprintAnalyticsSection = ({ data }: Readonly<{ data: BlueprintResponse }>) => {
  useAgentContext(data, byStrongFit(data.career_path_fit.categories));

  return (
    <Box sx={sectionSx}>
      <SubsectionHeader
        title={blueprint.title}
        subtitle={blueprint.subtitle}
        action={
          <LubavButton
            id="coaching-analytics-blueprint-discuss-lubav"
            label={appStrings.modules.coachingAnalytics.successSystem.discussWithLubav}
            promptButtonId="coaching_analytics_blueprint"
          />
        }
      />
      <Box sx={topRowSx}>
        <UniqueBlueprintsCard data={data} />
        <TargetCareerTracksCard data={data} />
      </Box>
      <CareerPathFitCard data={data} />
    </Box>
  );
};

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const topRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'stretch',
  '@container (min-width: 900px)': {
    gridTemplateColumns: 'minmax(0, 0.65fr) minmax(0, 1.35fr)',
  },
});

const innerPanelSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-2xl'],
  width: '100%',
  boxSizing: 'border-box',
});

const trackLegendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const trackLegendRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const legendRingSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const fitLegendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
});

const fitRowListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
  '@container (min-width: 650px)': {
    gap: backrs.spacing['spacing-lg'],
  },
});

const fitRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
  '@container (min-width: 650px)': {
    flexDirection: 'row',
    alignItems: 'center',
    gap: backrs.spacing['spacing-lg'],
  },
});

const fitRowLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  '@container (min-width: 650px)': {
    width: 208,
    flexShrink: 0,
  },
});

const fitBarWrapSx: SxProps<Theme> = {
  width: '100%',
  minWidth: 0,
  '@container (min-width: 650px)': {
    flex: 1,
  },
};
